import { mergeFlareNodeAttrs } from '@common/flare/prosemirror/transform/node';
import { findImageUploadPos, ImageActionType, ImagePlaceholderAction, ImageUploadMetaKey, ImageUploadPluginMetaData } from '@common/prosemirror/plugins/image-upload.plugin';
import { getTrackedChangesState, TrackedChangesPluginState, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { copySliceWithChanges } from '@common/prosemirror/tracked-changes/copy';
import { Attrs, Fragment, Node, ResolvedPos } from 'prosemirror-model';
import { Command, EditorState, Selection, TextSelection, Transaction } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

export type ImageNodeCommand = (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, attrs?: {}) => boolean;
// Insert a placeholder that gets overwritten after the image is hosted on the API server
export type CreateImagePlaceholdersCommand = (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, actions?: ImagePlaceholderAction[]) => boolean;

export interface ImageAction extends ImagePlaceholderAction {
  url?: string;
  attrs?: Attrs;
}

/*
 * Flare Commands
*/

// Used for inserting new images that must be added to the server first
export function insertImagePlaceholders(): CreateImagePlaceholdersCommand {
  return function (editorState: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, actions?: ImagePlaceholderAction[]): boolean {
    const selection: Selection = editorState.selection;
    const tr: Transaction = editorState.tr;

    if (dispatch) {
      const trackedChangesState: TrackedChangesPluginState = getTrackedChangesState(editorState);

      // Insert a tracked change removal
      if (!selection.empty) {
        tr.replaceSelection(
          copySliceWithChanges(trackedChangesState?.changeMarkType, selection.content(), trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Remove))
        );
      }

      actions.forEach(action => {
        // Default to the beginning of the selection if not assigned
        action.pos = action.pos ?? selection.from;
        action.isTrackedChangesEnabled = trackedChangesState?.enabled;
      });

      // Set the selection at the beginning of the selection
      tr.setSelection(TextSelection.near(tr.doc.resolve(selection.from), 1));

      // Send the metadata to update the placeholder
      tr.setMeta(ImageUploadMetaKey, {
        add: actions
      } as ImageUploadPluginMetaData);

      // Mark that the changes were tracked
      tr.setMeta(TrackedChangesTrackedMetaKey, true);

      dispatch(tr.scrollIntoView());
    }

    return true;
  };
}

/*
 * Image placeholder functions
*/

export function updateImagePlaceholders(actions: ImageAction[]): Command {
  return function (editorState: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView): boolean {
    return dispatchImageMeta(editorState, dispatch, ImageActionType.Remove, view, actions);
  };
}

export function insertImageUploadErrors(actions: ImagePlaceholderAction[]): Command {
  return function (editorState: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView): boolean {
    return dispatchImageMeta(editorState, dispatch, ImageActionType.Error, view, actions);
  };
}

export function removeImagePlaceholders(actions: ImagePlaceholderAction[]): Command {
  return function (editorState: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView): boolean {
    return dispatchImageMeta(editorState, dispatch, ImageActionType.Remove, view, actions);
  };
}

function dispatchImageMeta(state: EditorState, dispatch: ProsemirrorDispatcher, actionType: ImageActionType, view?: EditorView, actions?: ImageAction[]): boolean {
  // Find the placeholder position
  actions.forEach(action => {
    action.pos = findImageUploadPos(state, action.id);
  })

  if (actions.some(action => typeof action.pos !== 'number')) {
    return false;
  }

  if (dispatch) {
    const tr = state.tr;
    const imageType = state.schema.nodes.image;

    actions.forEach(action => {
      // Insert image node with url from server
      if (actionType === ImageActionType.Remove && action.url) {
        const $mappedPos: ResolvedPos = tr.doc.resolve(tr.mapping.map(action.pos));
        let actionAttrsCopy = action.attrs;
        if (action.isTrackedChangesEnabled) {
          const trackedChangesState = getTrackedChangesState(state);
          const addChanges = trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Add);
          actionAttrsCopy = {...action.attrs, ...addChanges};
        }
        const attrs: Attrs = mergeFlareNodeAttrs(actionAttrsCopy, { src: action.url });
        const imageNode: Node = imageType.create(attrs);

        tr.insert($mappedPos.pos, imageNode);

        // Image must be wrapped in a block
        if (!$mappedPos.parent.canReplace($mappedPos.index(), $mappedPos.index(), Fragment.from(imageNode))) {
          // Update the selection to the inside of the block after the image
          tr.setSelection(Selection.near(tr.doc.resolve($mappedPos.pos + 2), -1));
        }
      }
    });
    // Remove the placeholderss
    tr.setMeta(ImageUploadMetaKey, {
      [actionType]: actions
    } as ImageUploadPluginMetaData);

    // Mark that the changes were tracked
    if (actions.some(action => action.isTrackedChangesEnabled)) {
      tr.setMeta(TrackedChangesTrackedMetaKey, true);
    }

    dispatch(tr);
  }

  return true;
}
